import React, { useState } from 'react';
import Card, { CardRow } from 'components/styleguide/card/Card';
import Sheet from 'components/styleguide/sheet_modal/Sheet';
import InputField from 'components/styleguide/input_field/InputField';
import Checkbox from 'components/styleguide/checkbox/Checkbox';
import { useEditableSheet } from 'components/styleguide/sheet_modal/useEditableSheet';
import SelectField from 'components/styleguide/select_field/SelectField';
import LocationField from 'components/styleguide/location_field/LocationField';
import Icon from 'components/styleguide/icon/Icon';
const styles = require('../onboarding-cards.module.scss');

interface SelectOption {
  label: string;
  value: string;
}

interface ProfileDetailsCardProps {
  name: string;
  city: string;
  country_code: string;
  country_name: string;
  companySize: SelectOption;
  industry: SelectOption;
  fullRemote: boolean;
  sizeOptions: SelectOption[];
  industryOptions: SelectOption[];
  url: string;
}

const ProfileDetailsCard: React.FC<ProfileDetailsCardProps> = ({
  name,
  city,
  country_code,
  country_name,
  companySize,
  industry,
  fullRemote,
  sizeOptions,
  industryOptions,
  url,
}) => {
  const [display, setDisplay] = useState({
    name,
    city,
    countryCode: country_code,
    countryName: country_name,
    sizeId: companySize.value,
    sizeLabel: companySize.label,
    industryId: industry.value,
    industryName: industry.label,
    fullRemote: fullRemote || false,
  });

  const sheet = useEditableSheet({
    url,
    payloadKey: 'company',
    onSuccess: (formData) => {
      setDisplay({
        name: formData.name,
        city: formData.city,
        countryCode: formData.country_code,
        countryName: formData.country_name,
        sizeId: formData.company_size_id,
        sizeLabel: sizeOptions.find(o => o.value === formData.company_size_id)?.label || display.sizeLabel,
        industryId: formData.industry_id,
        industryName: industryOptions.find(o => o.value === formData.industry_id)?.label || display.industryName,
        fullRemote: formData.full_remote,
      });
    },
  });

  const displayMap: Record<string, any> = {
    name: display.name,
    city: display.city,
    country_code: display.countryCode,
    company_size_id: display.sizeId,
    industry_id: display.industryId,
  };

  const missingIcon = (...fields: string[]) =>
    fields.some(f => !displayMap[f]) ? <Icon name="circleAlert" size="xs" color="warning" /> : null;

  const hasAny = !!(display.name || display.city || display.sizeLabel || display.industryName);
  const hasAll = !!(display.name && display.city && display.sizeLabel && display.industryName);

  const handleEditClick = () => {
    sheet.open('Edit profile details', {
      name: display.name,
      city: display.city,
      country_code: display.countryCode,
      country_name: display.countryName,
      company_size_id: display.sizeId,
      industry_id: display.industryId,
      full_remote: display.fullRemote,
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    sheet.handleFormChange(e.target.name, e.target.value);
  };

  const handleSelectChange = (name: string) => (value: any) => {
    sheet.handleFormChange(name, value?.value);
  };

  const handlelocationChange = (e: any) => {
    const addressArray = e.formatted_address.split(', ');
    const city = addressArray[0];
    const countryComponent = e.address_components.find(
      (component: any) => component.types.includes('country')
    );
    const countryShort = countryComponent?.short_name || '';
    const countryLong = countryComponent?.long_name || '';

    sheet.handleFormChange('city', city);
    sheet.handleFormChange('country_code', countryShort);
    sheet.handleFormChange('country_name', countryLong);
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    sheet.handleFormChange(e.target.name, e.target.checked);
  };

  return (
    <>
      <Card
        title="Profile details"
        iconName="file"
        context="company"
        tag={hasAll ? undefined : hasAny
          ? { variant: 'warning', icon: 'circleAlert', children: 'Incomplete' }
          : { variant: 'warning', icon: 'circleAlert', children: 'Missing' }}
        onEdit={handleEditClick}
      >
        <CardRow context="company">
          <div className={styles.fieldGrid}>
            <div className={styles.field}>
              <p className={styles.label}>
                Company name
                {missingIcon('name')}
              </p>
              <p className={styles.value}>{display.name || '-'}</p>
            </div>
            <div className={styles.field}>
              <p className={styles.label}>
                Location
                {missingIcon('city', 'country_code')}
              </p>
              <p className={styles.value}>{[display.city, display.countryName].filter(Boolean).join(', ') || '-'}</p>
            </div>
            <div className={styles.field}>
              <p className={styles.label}>
                Size
                {missingIcon('company_size_id')}
              </p>
              <p className={styles.value}>{display.sizeLabel || '-'}</p>
            </div>
            <div className={styles.field}>
              <p className={styles.label}>
                Industry
                {missingIcon('industry_id')}
              </p>
              <p className={styles.value}>{display.industryName || '-'}</p>
            </div>
          </div>
        </CardRow>
      </Card>

      {sheet.isOpen && (
        <Sheet
          title={sheet.sheetTitle}
          onClose={sheet.close}
          context="company"
          showCancelButton={false}
          continueButtonText="Save"
          onContinue={sheet.handleSave}
        >
          <div className={styles.form}>
            <InputField
              label="Company name"
              placeholder="e.g. Company name, Lda."
              name="name"
              required={true}
              value={sheet.formData.name || ''}
              onChange={handleInputChange}
              size="L"
              error={sheet.getError('name')}
            />
            <SelectField
              label="Size"
              name="company_size_id"
              placeholder="Select company size"
              options={sizeOptions}
              value={sizeOptions.find(o => o.value === sheet.formData.company_size_id)}
              onChange={handleSelectChange('company_size_id')}
              size="L"
              error={sheet.getError('company_size_id')}
            />

            <SelectField
              label="Industry"
              name="industry_id"
              placeholder="Select company industry"
              options={industryOptions}
              isMulti={false}
              value={industryOptions.find(o => o.value === sheet.formData.industry_id)}
              onChange={handleSelectChange('industry_id')}
              size="L"
              error={sheet.getError('industry_id')}
            />
            <LocationField
              error={sheet.getError('city') || sheet.getError('country_code')}
              onLocationChange={handlelocationChange}
              defaultValue={{"city": sheet.formData.city, "countryCode": sheet.formData.country_code, "country": sheet.formData.country_name}}
              value={{"city": sheet.formData.city, "countryCode": sheet.formData.country_code, "country": sheet.formData.country_name}}
              label={'Location'}
            />
            <Checkbox
              id="full-remote-checkbox"
              name="full_remote"
              label="Full remote company"
              checked={!!sheet.formData.full_remote}
              onChange={handleCheckboxChange}
            />
          </div>
        </Sheet>
      )}
    </>
  );
};

export default ProfileDetailsCard;
